import { type ReactNode, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  canClubEditTrip,
  isAmendedTrip,
  type ProposalDto,
  type RoomConfig,
  type TripDto,
  type TripStatus,
} from "@tc/shared";
import {
  Button,
  Card,
  CardTitle,
  Icon,
  type IconName,
  Modal,
  StatusBadge,
  Tabs,
  type TabOption,
  TRIP_STATUS_BADGE,
} from "@/components/ui";
import { Can, useAuthz } from "@/lib/auth/authz";
import { useFormatters } from "@/lib/i18n/format";
import { ManifestTab } from "@/features/manifest/ManifestTab";
import { MessagesTab } from "@/features/messaging/MessagesTab";
import { ProposalPdfButton, ProposalTab } from "@/features/proposal/ProposalTab";
import { ClubDecisionModal } from "@/features/proposal/ClubDecisionModal";
import { AuditTab } from "@/features/notes/AuditTab";
import { NotesTab } from "@/features/notes/NotesTab";
import { DocumentsPanel, DocumentsTab } from "@/features/records/DocumentsTab";
import { proposalApi } from "@/features/proposal/api";
import { ApiError, tripsApi } from "./api";
import { BOARD_LABEL, EXTRA_LABEL, TRANSPORT_LABEL } from "./RequestSummaryCard";
import { STATUS_KEY } from "./status";
import { resolveTripView } from "./trip-view";
import { BoStateBanner, type BoProposalVariant, TripOverviewBo } from "./TripOverviewBo";
import { TransportIcons } from "./TripCard";

type TabKey =
  | "overview"
  | "proposal"
  | "roster"
  | "messages"
  | "documents"
  | "invoices"
  | "notes"
  | "audit";

const TABS: { value: TabKey; labelKey: string; icon: IconName }[] = [
  { value: "overview", labelKey: "trips.tabOverview", icon: "dashboard" },
  { value: "proposal", labelKey: "trips.tabProposal", icon: "doc" },
  { value: "roster", labelKey: "trips.tabRoster", icon: "roster" },
  { value: "messages", labelKey: "trips.tabMessages", icon: "messages" },
  { value: "documents", labelKey: "trips.tabDocuments", icon: "download" },
  { value: "invoices", labelKey: "trips.tabInvoices", icon: "invoices" },
];

/** Back-office-only tabs: TC internal notes + the per-trip audit trail (TW-3457). */
const BACK_OFFICE_TABS: { value: TabKey; labelKey: string; icon: IconName }[] = [
  { value: "notes", labelKey: "trips.tabNotes", icon: "edit" },
  { value: "audit", labelKey: "trips.tabAudit", icon: "clock" },
];

/**
 * The club's Accepted/Completed tabbed view (the agreed-trip state). The Overview shows the
 * accepted-proposal summary (cost breakdown + key deadlines), not the enquiry detail cards; the
 * passenger list, the TC conversation and the trip documents sit alongside. There is no standalone
 * Proposal tab (the proposal *is* the Overview) and no Invoices tab (invoices live on Documents and
 * the club's own Invoices page); notes/audit stay back-office.
 */
const CLUB_AGREED_TABS: { value: TabKey; labelKey: string; icon: IconName }[] = [
  { value: "overview", labelKey: "trips.tabOverview", icon: "dashboard" },
  { value: "roster", labelKey: "trips.tabPassengerList", icon: "roster" },
  { value: "messages", labelKey: "trips.tabMessages", icon: "messages" },
  { value: "documents", labelKey: "trips.tabDocuments", icon: "download" },
];

/**
 * The back-office STATE 2 (read-only, tabbed) tab list, stage-aware. The proposal summary now lives
 * in Overview, so there is no standalone "Proposal" tab. The roster ("Passenger list") appears once
 * the club has accepted the proposal (the agreed state — it's actionable then). The Documents tab
 * already carries both the documents and the invoices sections, so there is no separate Invoices tab.
 * This is the template for every non-editing back-office trip state.
 */
function boState2Tabs(variant: BoProposalVariant): { value: TabKey; labelKey: string; icon: IconName }[] {
  const tabs: { value: TabKey; labelKey: string; icon: IconName }[] = [
    { value: "overview", labelKey: "trips.tabOverview", icon: "dashboard" },
  ];
  // Roster only exists once the trip is agreed (accepted/completed) — a still-sent or expired
  // proposal has no confirmed passengers to manage.
  if (variant === "accepted" || variant === "completed")
    tabs.push({ value: "roster", labelKey: "trips.tabPassengerList", icon: "roster" });
  tabs.push({ value: "messages", labelKey: "trips.tabMessages", icon: "messages" });
  tabs.push({ value: "documents", labelKey: "trips.tabDocuments", icon: "download" });
  tabs.push({ value: "notes", labelKey: "trips.tabNotes", icon: "edit" });
  tabs.push({ value: "audit", labelKey: "trips.tabAudit", icon: "clock" });
  return tabs;
}

/**
 * Lifecycle action a `trip.write` holder can take from the header, by current status. `delete`
 * archives an enquiry and `archive` archives a finished (Completed) trip — both soft-delete via the
 * same endpoint; `cancel`/`postpone` are status transitions (the server enforces the same state
 * machine). Editing an enquiry is offered separately (it navigates to the wizard, not a confirm).
 * `complete` closes out an Accepted trip once it has been travelled (TRAV-72) — a back-office call,
 * so it is only offered there (the API refuses it from a club user). A Completed trip's only action
 * is Archive; the other terminal states expose none.
 */
type TripAction = "delete" | "cancel" | "postpone" | "complete" | "archive";

function actionsForStatus(status: TripStatus, isBackOffice: boolean): TripAction[] {
  switch (status) {
    case "Enquiry":
      return ["delete"];
    case "ProposalSent":
      return ["cancel", "postpone"];
    case "Accepted":
      return isBackOffice ? ["complete", "postpone"] : ["postpone"];
    case "Completed":
      return ["archive"];
    default:
      return [];
  }
}

/** The target status a transition action moves the trip to (delete/archive are not transitions). */
const ACTION_TARGET: Record<Exclude<TripAction, "delete" | "archive">, TripStatus> = {
  cancel: "Cancelled",
  postpone: "Postponed",
  complete: "Completed",
};

const ROOM_LABEL: Record<keyof RoomConfig, string> = {
  single: "request.fSingle",
  double: "request.fDouble",
  twin: "request.fTwin",
  triple: "request.fTriple",
};

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; trip: TripDto; proposal: ProposalDto | null }
  | { kind: "notFound" }
  | { kind: "error" };

/**
 * The club's Accepted/Completed Overview: an accepted-trip header (title + "Proposal accepted" pill)
 * over the read-only proposal summary — the cost breakdown the club confirmed and the key deadlines.
 * Reuses the club proposal view (with its aside accepted-card suppressed, since the header carries
 * the accepted signal). A completed trip reads the same, with closed-out copy.
 */
function ClubAgreedOverview({
  trip,
  completed,
  onVersion,
}: {
  trip: TripDto;
  completed: boolean;
  onVersion: (version: number) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="stack-lg">
      <div className="row-between" style={{ alignItems: "flex-start", gap: "var(--gap)" }}>
        <div>
          <h2 className="agreed-title">
            {completed ? t("proposal.completedOverviewTitle") : t("proposal.acceptedOverviewTitle")}
          </h2>
          <p className="sub" style={{ margin: "4px 0 0" }}>
            {completed ? t("proposal.completedOverviewSub") : t("proposal.acceptedOverviewSub")}
          </p>
        </div>
        <span className="agreed-pill">
          <Icon name="checkCircle" size={15} />
          {completed ? t("proposal.completed") : t("proposal.accepted")}
        </span>
      </div>
      <ProposalTab trip={trip} onVersion={onVersion} hideStatusCard />
    </div>
  );
}

/**
 * Trip detail (TW-3439). Opens any trip the club owns; a trip belonging to another club returns
 * 404 from `GET /api/trips/:id` via RLS, which we render as a not-found state rather than crashing.
 * The Overview tab is populated now from the TripDto; the remaining tabs (hotels, proposal, roster,
 * messaging, documents, invoices) are scaffolded for the modules that fill them in later.
 */
export function TripDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isBackOffice } = useAuthz();
  const { id = "" } = useParams<{ id: string }>();
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  // The active tab lives in the URL (`?tab=`) so browser back/forward step through tabs and a tab
  // is deep-linkable. An absent/invalid value just renders the default first tab — it is never
  // normalized with a navigation, so a stale link can't create redirect loops or history noise.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab");
  const setTab = useCallback(
    (next: TabKey) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set("tab", next);
          return params;
        },
        // Default (push) navigation: each tab click is a history entry back/forward traverses.
      );
    },
    [setSearchParams],
  );
  const [action, setAction] = useState<TripAction | null>(null);
  // Back office recording the club's accept/reject for it (TRAV-74).
  const [clubDecisionOpen, setClubDecisionOpen] = useState(false);
  // Proposal version, lifted out of the club ProposalSent view so it can sit in the page header
  // next to the status badge (the review reports it once the proposal loads).
  const [proposalVersion, setProposalVersion] = useState<number | null>(null);
  // Back-office proposal lock state + revise handler, reported up by the builder so the Revise button
  // can sit in the page header with the other lifecycle actions.
  // Back-office explicit "edit the proposal" mode. The view is driven by TRIP status — a sent trip
  // (ProposalSent/Accepted/…) shows STATE 2 by default — and Revise flips into STATE 1 (the builder)
  // for this session. It resets when navigating to another trip, so reopening a sent trip lands on
  // STATE 2 again (the in-progress Draft revision is preserved server-side and resumed via Revise).
  const [editMode, setEditMode] = useState(false);
  // A pending club amendment lands back office straight in the builder (so it gets answered);
  // this override is their explicit "show me the trip anyway" escape back to the tabbed view.
  const [boTabbedOverride, setBoTabbedOverride] = useState(false);
  useEffect(() => {
    setEditMode(false);
    setBoTabbedOverride(false);
  }, [id]);

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    setProposalVersion(null);
    try {
      const [trip, proposal] = await Promise.all([tripsApi.get(id), proposalApi.get(id)]);
      setState({ kind: "ready", trip, proposal });
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) setState({ kind: "notFound" });
      else setState({ kind: "error" });
    }
  }, [id]);

  // Page-level Revise (STATE 2 → STATE 1): just enter edit mode — nothing is persisted (UAT round
  // 3). The builder loads the latest version (a live one to revise, or an in-progress Draft to
  // resume) and the *first save* is what creates the revision vN+1 server-side; backing out leaves
  // no stray version behind.
  const onReviseProposal = useCallback(() => setEditMode(true), []);

  // Cancel the revise session: back to STATE 2 without saving. Anything already saved (a persisted
  // Draft revision) is kept server-side and resumed on the next Revise; unsaved edits are dropped.
  const onCancelRevise = useCallback(() => {
    setEditMode(false);
    void load();
  }, [load]);

  // The builder reports a successful send up here so the page leaves edit mode and reloads — the trip
  // is now ProposalSent, so the determinant lands on STATE 2.
  const onProposalSent = useCallback(() => {
    setEditMode(false);
    void load();
  }, [load]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.kind === "loading") {
    return (
      <div className="page">
        <div className="empty">{t("common.loading")}</div>
      </div>
    );
  }

  if (state.kind === "notFound" || state.kind === "error") {
    return (
      <div className="page">
        <BackLink />
        <div className="empty">
          <div className="empty-ic">
            <Icon name="trips" size={24} />
          </div>
          <div style={{ fontWeight: 600, marginTop: 4 }}>
            {t(state.kind === "notFound" ? "trips.notFoundTitle" : "trips.loadError")}
          </div>
          {state.kind === "notFound" && <div className="sub">{t("trips.notFoundDesc")}</div>}
          <Button variant="soft" style={{ marginTop: 14 }} onClick={() => navigate("/trips")}>
            {t("trips.backToTrips")}
          </Button>
        </div>
      </div>
    );
  }

  const trip = state.trip;
  const proposal = state.proposal;
  // Which view this (audience, status) gets is resolved by a single pure, exhaustive function — see
  // trip-view.ts. It replaced a chain of booleans with a silent `else` fallback that once let the
  // club Accepted/Completed view go missing. The flags below are thin reads off that one decision so
  // the JSX downstream stays readable.
  //  • club-enquiry  — single scrolling page (trip details + TC conversation), no tabs.
  //  • club-proposal — the proposal review/accept one-pager (ProposalSent).
  //  • club-agreed   — tabbed agreed-trip view; Overview = the accepted-proposal summary.
  //  • bo-builder    — back-office STATE 1, the one-page builder (Enquiry, or Revise/editMode).
  //  • bo-tabbed     — back-office STATE 2, the read-only tabbed trip view.
  const view = resolveTripView({
    isBackOffice,
    status: trip.status,
    boEditMode: editMode,
    amended: isAmendedTrip(trip),
    boTabbedOverride,
  });
  const isClubEnquiry = view === "club-enquiry";
  const isClubProposalSent = view === "club-proposal";
  const isClubAgreed = view === "club-agreed";
  const isBoEditingProposal = view === "bo-builder";
  const isBoTabbedView = view === "bo-tabbed";
  // The STATE 2 stage drives the Overview banner/total semantics and the tab list — from the TRIP
  // status (so an in-progress Draft revision doesn't change the sent trip's framing). `sent` is the
  // proposal still with the club; `expired` is a lapsed proposal the club never accepted (TRAV-64 —
  // MUST NOT read as accepted: no selection, options shown unchosen, Revise CTA); `completed` is the
  // closed-out, read-only trip (locked roster, only an Archive action); everything else (Accepted +
  // the remaining terminal states) is `accepted`.
  const boVariant: BoProposalVariant =
    trip.status === "ProposalSent"
      ? "sent"
      : trip.status === "Expired"
        ? "expired"
        : trip.status === "Completed"
          ? "completed"
          : "accepted";
  const allTabs = isBoTabbedView
    ? boState2Tabs(boVariant)
    : isClubAgreed
      ? CLUB_AGREED_TABS
      : isBackOffice
        ? [...TABS, ...BACK_OFFICE_TABS]
        : TABS;
  const tabOptions: TabOption<TabKey>[] = allTabs.map((tb) => ({
    value: tb.value,
    label: t(tb.labelKey),
    icon: tb.icon,
  }));
  // The URL's ?tab only counts when it names a tab available in the CURRENT tab set (audience and
  // trip state change the set); anything else silently renders the default first tab.
  const activeTab: TabKey =
    allTabs.find((tb) => tb.value === tab)?.value ?? allTabs[0]?.value ?? "overview";

  return (
    <div className="page">
      <BackLink />
      <TripHeader
        trip={trip}
        onAction={setAction}
        onClubDecision={() => setClubDecisionOpen(true)}
        onEdit={() => navigate(`/trips/${trip.id}/edit`)}
        versionLabel={
          (isClubProposalSent || isClubAgreed) && proposalVersion != null
            ? `${t("proposal.version")} ${proposalVersion}`
            : isBoEditingProposal && (proposalVersion ?? proposal?.version) != null
              ? // While editing, the builder reports the live version (save bumps it to the new Draft).
                `${t("proposal.version")} ${proposalVersion ?? proposal?.version}`
              : isBoTabbedView && proposal?.version != null
                ? // STATE 2 header names the version the CLUB is on: an unsent Draft revision as the
                  // latest means the club still sees the version before it (the banner explains).
                  `${t("proposal.version")} ${proposal.status === "Draft" && proposal.version > 1 ? proposal.version - 1 : proposal.version}`
                : undefined
        }
        headerActions={
          isClubProposalSent ? (
            <ProposalPdfButton tripId={trip.id} />
          ) : isBoTabbedView &&
            (boVariant === "sent" || boVariant === "accepted" || boVariant === "expired") ? (
            // STATE 2 page-level Revise: flip to STATE 1 (the builder). Nothing is persisted until
            // the builder saves — the first save creates the revision Draft (UAT round 3).
            <Button variant="primary" icon="edit" onClick={onReviseProposal}>
              {t("builder.revise")}
            </Button>
          ) : isBoEditingProposal && editMode ? (
            // Leave the revise session (UAT round 3): back to STATE 2; unsaved edits are dropped, a
            // saved Draft revision is kept server-side and resumed on the next Revise.
            <Button variant="soft" icon="x" onClick={onCancelRevise}>
              {t("builder.cancelRevise")}
            </Button>
          ) : undefined
        }
      />
      {isClubEnquiry ? (
        <>
          <div className="info-banner" role="status">
            <Icon name="clock" size={16} />
            <span>{t("trips.enquiryBanner")}</span>
          </div>
          <div className="stack-lg" style={{ marginTop: "var(--gap)" }}>
            <OverviewTab trip={trip} />
            <MessagesTab
              trip={trip}
              header={{ icon: "messages", title: t("messages.withTc"), subtitle: t("messages.withTcSub") }}
            />
          </div>
        </>
      ) : isClubProposalSent ? (
        <div style={{ marginTop: "var(--gap)" }}>
          <ProposalTab trip={trip} onVersion={setProposalVersion}>
            <DocumentsPanel trip={trip} />
            <MessagesTab
              trip={trip}
              header={{ icon: "messages", title: t("messages.withTc"), subtitle: t("messages.withTcSub") }}
            />
          </ProposalTab>
        </div>
      ) : isBoEditingProposal ? (
        <div style={{ marginTop: "var(--gap)" }}>
          {isAmendedTrip(trip) && (
            // The club amended the request after the proposal went out — that's what landed the
            // operator here. Publishing a version (send / apply-update) clears the marker.
            <div className="info-banner" role="status" style={{ marginBottom: "var(--gap)" }}>
              <Icon name="alert" size={16} />
              <span>
                <b>{t("builder.amendedTitle")}</b> — {t("builder.amendedDesc")}
              </span>
              {!editMode && (
                <Button
                  variant="ghost"
                  size="sm"
                  style={{ marginLeft: "auto", flexShrink: 0 }}
                  onClick={() => setBoTabbedOverride(true)}
                >
                  {t("builder.amendedViewTrip")}
                </Button>
              )}
            </div>
          )}
          <ProposalTab
            trip={trip}
            onVersion={setProposalVersion}
            onSent={onProposalSent}
          />
        </div>
      ) : (
        <>
          <Tabs
            options={tabOptions}
            value={activeTab}
            // Re-clicking the active tab is a no-op — don't push a duplicate history entry.
            onChange={(next) => next !== activeTab && setTab(next)}
          />
          <div style={{ marginTop: "var(--gap)" }}>
            {activeTab === "overview" ? (
              isBoTabbedView && proposal ? (
                <TripOverviewBo trip={trip} proposal={proposal} variant={boVariant} />
              ) : isClubAgreed && proposal ? (
                <ClubAgreedOverview
                  trip={trip}
                  completed={trip.status === "Completed"}
                  onVersion={setProposalVersion}
                />
              ) : (
                // A completed trip with no proposal (e.g. a historical invoice-import trip) has no
                // club selection to show, but still reads as closed-out: lead with the completed
                // banner, then the generic trip-detail cards.
                <>
                  {isBoTabbedView && boVariant === "completed" && <BoStateBanner variant="completed" />}
                  <OverviewTab trip={trip} />
                </>
              )
            ) : activeTab === "proposal" ? (
              <ProposalTab trip={trip} />
            ) : activeTab === "roster" ? (
              // A completed trip's passenger list is a locked, read-only record — no assigning.
              <ManifestTab trip={trip} locked={trip.status === "Completed"} />
            ) : activeTab === "messages" ? (
              <MessagesTab trip={trip} />
            ) : activeTab === "notes" && isBackOffice ? (
              <NotesTab trip={trip} />
            ) : activeTab === "audit" && isBackOffice ? (
              <AuditTab trip={trip} />
            ) : (
              <DocumentsTab trip={trip} />
            )}
          </div>
        </>
      )}

      {action && (
        <ActionConfirm
          trip={trip}
          action={action}
          onClose={() => setAction(null)}
          onDeleted={() => navigate("/trips")}
          onTransitioned={() => {
            setAction(null);
            // Refetch the trip *and* the proposal so the proposal-aware determinant stays correct
            // after a transition (e.g. ProposalSent → Accepted flips the STATE 2 variant/tabs).
            void load();
          }}
        />
      )}

      {clubDecisionOpen && (
        <ClubDecisionModal
          trip={trip}
          onClose={() => setClubDecisionOpen(false)}
          onDone={() => {
            setClubDecisionOpen(false);
            // The trip is now Accepted — refetch trip + proposal so the view lands on the agreed state.
            void load();
          }}
        />
      )}
    </div>
  );
}

function BackLink() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <Button
      variant="ghost"
      size="sm"
      icon="chevL"
      style={{ marginBottom: 14, paddingLeft: 4 }}
      onClick={() => navigate("/trips")}
    >
      {t("trips.title")}
    </Button>
  );
}

function TripHeader({
  trip,
  onAction,
  onClubDecision,
  onEdit,
  versionLabel,
  headerActions,
}: {
  trip: TripDto;
  onAction: (action: TripAction) => void;
  /** Back office: open the "Record club decision" dialog (TRAV-74). */
  onClubDecision: () => void;
  onEdit: () => void;
  /** A proposal-version tag shown next to the status badge (the ProposalSent one-page header). */
  versionLabel?: string;
  /** Extra header actions on the right, shown for everyone (e.g. Download PDF), not `trip.write`-gated. */
  headerActions?: ReactNode;
}) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const { isBackOffice } = useAuthz();
  const dest = [trip.destinationCity, trip.destinationCountry ? fmt.country(trip.destinationCountry) : ""].filter(Boolean).join(", ");
  const dates =
    trip.travelStartDate && trip.travelEndDate
      ? `${fmt.date(trip.travelStartDate)} – ${fmt.date(trip.travelEndDate)}`
      : trip.travelStartDate
        ? fmt.date(trip.travelStartDate)
        : null;
  const sub = [trip.competition, dest, dates].filter(Boolean).join(" · ");
  const actions = actionsForStatus(trip.status, isBackOffice);

  return (
    <div className="row-between page-head" style={{ alignItems: "flex-start" }}>
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <span className="t-id mono">{trip.reference}</span>
          <StatusBadge status={TRIP_STATUS_BADGE[trip.status]} label={t(STATUS_KEY[trip.status])} />
          {isBackOffice && isAmendedTrip(trip) && (
            // A pending club amendment TC hasn't answered yet — clears when a version is published.
            <StatusBadge status={TRIP_STATUS_BADGE.Enquiry} label={t("trips.amendedBadge")} />
          )}
          {versionLabel && <span className="tag">{versionLabel}</span>}
        </div>
        <h1>{trip.matchOpponent ?? trip.sport ?? trip.reference}</h1>
        {sub && <div className="sub">{sub}</div>}
      </div>
      <div style={{ display: "flex", gap: 8, flexShrink: 0, alignItems: "center" }}>
        {headerActions}
        <Can permission="trip.write">
          {/* Editing the request belongs to the club that raised it — right up until the trip
              completes (UAT round 3 follow-up: post-proposal edits stamp the amendment marker and
              notify TC). Back office engages by building a proposal, not by rewriting the enquiry.
              An Expired trip's edit entry is the Resubmit CTA on its proposal view, not here. */}
          {!isBackOffice && canClubEditTrip(trip.status) && trip.status !== "Expired" && (
            <Button variant="secondary" icon="edit" onClick={onEdit}>
              {t("trips.editRequest")}
            </Button>
          )}
          {/* The club answered TC outside the Travel Desk (phone/email) but never responded here —
              back office records the accept/reject for it, including on a lapsed proposal (TRAV-74). */}
          {isBackOffice && (trip.status === "ProposalSent" || trip.status === "Expired") && (
            <Button variant="secondary" icon="checkCircle" onClick={onClubDecision}>
              {t("proposal.onBehalf.action")}
            </Button>
          )}
          {actions.map((a) => (
            <Button
              key={a}
              variant={a === "complete" ? "primary" : a === "postpone" || a === "archive" ? "secondary" : "danger"}
              icon={a === "delete" || a === "archive" ? "trash" : a === "cancel" ? "x" : a === "complete" ? "check" : "clock"}
              onClick={() => onAction(a)}
            >
              {t(`trips.action${a.charAt(0).toUpperCase()}${a.slice(1)}`)}
            </Button>
          ))}
        </Can>
      </div>
    </div>
  );
}

/** Per-action confirmation copy + button styling for the header lifecycle actions. */
const ACTION_COPY: Record<
  TripAction,
  { title: string; body: string; confirm: string; variant: "danger" | "primary" }
> = {
  delete: {
    title: "trips.confirmDeleteTitle",
    body: "trips.confirmDeleteBody",
    confirm: "trips.confirmDeleteConfirm",
    variant: "danger",
  },
  cancel: {
    title: "trips.confirmCancelTitle",
    body: "trips.confirmCancelBody",
    confirm: "trips.confirmCancelConfirm",
    variant: "danger",
  },
  postpone: {
    title: "trips.confirmPostponeTitle",
    body: "trips.confirmPostponeBody",
    confirm: "trips.confirmPostponeConfirm",
    variant: "primary",
  },
  complete: {
    title: "trips.confirmCompleteTitle",
    body: "trips.confirmCompleteBody",
    confirm: "trips.confirmCompleteConfirm",
    variant: "primary",
  },
  archive: {
    title: "trips.confirmArchiveTitle",
    body: "trips.confirmArchiveBody",
    confirm: "trips.confirmArchiveConfirm",
    variant: "primary",
  },
};

/**
 * Confirmation dialog for a header lifecycle action. `delete` soft-deletes (archives) the trip and
 * the caller navigates away; `cancel`/`postpone` POST a status transition and hand the updated trip
 * back so the page re-renders in place.
 */
function ActionConfirm({
  trip,
  action,
  onClose,
  onDeleted,
  onTransitioned,
}: {
  trip: TripDto;
  action: TripAction;
  onClose: () => void;
  onDeleted: () => void;
  onTransitioned: (updated: TripDto) => void;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const copy = ACTION_COPY[action];

  async function onConfirm() {
    setError(null);
    setBusy(true);
    try {
      if (action === "delete" || action === "archive") {
        await tripsApi.remove(trip.id);
        onDeleted();
      } else {
        const updated = await tripsApi.transition(trip.id, { status: ACTION_TARGET[action] });
        onTransitioned(updated);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("trips.actionError"));
      setBusy(false);
    }
  }

  return (
    <Modal open onClose={onClose} title={t(copy.title)}>
      <div className="modal-body">
        {error && (
          <div className="auth-alert" style={{ marginBottom: 16 }} role="alert">
            <Icon name="alert" size={16} className="aa-ic" />
            <div className="aa-body">{error}</div>
          </div>
        )}
        <p className="muted" style={{ margin: 0 }}>
          {t(copy.body)}
        </p>
      </div>
      <div className="modal-foot">
        <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
          {t("actions.cancel")}
        </Button>
        <Button type="button" variant={copy.variant} onClick={onConfirm} disabled={busy}>
          {t(copy.confirm)}
        </Button>
      </div>
    </Modal>
  );
}

function Kv({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="kv">
      <span className="k">{label}</span>
      <span className="v">{children}</span>
    </div>
  );
}

function OverviewTab({ trip }: { trip: TripDto }) {
  const { t } = useTranslation();
  const fmt = useFormatters();

  const transports = trip.transportModes
    ? (["flight", "train", "coach", "hireCar"] as const).filter((m) => trip.transportModes?.[m])
    : [];
  const rooms = trip.roomConfig
    ? (Object.keys(ROOM_LABEL) as (keyof RoomConfig)[])
        .filter((k) => (trip.roomConfig?.[k] ?? 0) > 0)
        .map((k) => `${trip.roomConfig?.[k]} ${t(ROOM_LABEL[k]).toLowerCase()}`)
    : [];
  const extras = trip.extraServices ?? [];
  const dates =
    trip.travelStartDate && trip.travelEndDate
      ? `${fmt.date(trip.travelStartDate)} – ${fmt.date(trip.travelEndDate)}`
      : trip.travelStartDate
        ? fmt.date(trip.travelStartDate)
        : "—";
  // Total pax, with the players/staff split in brackets when the breakdown was captured.
  const paxLabel =
    trip.paxCount == null
      ? "—"
      : trip.playerCount != null && trip.staffCount != null
        ? `${trip.paxCount} (${trip.playerCount} ${t("request.fPlayers").toLowerCase()}, ${trip.staffCount} ${t("request.fStaff").toLowerCase()})`
        : String(trip.paxCount);

  return (
    <div className="grid-3">
      <Card>
        <CardTitle>{t("trips.secTravel")}</CardTitle>
        <div className="kv-grid" style={{ gridTemplateColumns: "1fr", marginTop: 14 }}>
          <Kv label={t("request.fTransport")}>
            {transports.length > 0 ? (
              <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <TransportIcons modes={trip.transportModes} />
                {transports.map((m) => t(TRANSPORT_LABEL[m])).join(", ")}
              </span>
            ) : (
              "—"
            )}
          </Kv>
          <Kv label={t("trips.colPax")}>{paxLabel}</Kv>
          <Kv label={t("trips.colDates")}>{dates}</Kv>
          <Kv label={t("request.fBaggage")}>{trip.baggageRequirements?.notes || "—"}</Kv>
        </div>
      </Card>

      <Card>
        <CardTitle>{t("trips.secRooms")}</CardTitle>
        <div className="kv-grid" style={{ gridTemplateColumns: "1fr", marginTop: 14 }}>
          <Kv label={t("request.fRooms")}>{rooms.length > 0 ? rooms.join(", ") : "—"}</Kv>
          <Kv label={t("request.fBoard")}>{trip.boardBasis ? t(BOARD_LABEL[trip.boardBasis]) : "—"}</Kv>
          <Kv label={t("request.fExtras")}>
            {extras.length > 0 ? extras.map((x) => t(EXTRA_LABEL[x])).join(", ") : "—"}
          </Kv>
        </div>
      </Card>

      <Card>
        <CardTitle>{t("trips.secNotes")}</CardTitle>
        <div className="kv-grid" style={{ gridTemplateColumns: "1fr", marginTop: 14 }}>
          <Kv label={t("request.fHotelPref")}>{trip.hotelPreferenceNotes || "—"}</Kv>
          <Kv label={t("request.fNotes")}>{trip.notes || "—"}</Kv>
        </div>
      </Card>
    </div>
  );
}

